import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import StandardizationString from "@/utils/standardization_string";
import { markInvalidFields } from "@/validation/markInvalidFields";

/**
 * SCRUM-2127: the Update Categories form state, written as a createSlice like
 * every reducer in this client (SCRUM-1954), but mounted LOCALLY with
 * `useReducer(categoryForm.reducer, ...)` — it is the open panel's unsaved
 * draft, not store data, and it is gone when the screen unmounts. The
 * categories themselves stay in the store's `categorySlice`.
 */

/** One country's entry on a subcategory's `properties[]` (Catalog_System.md D8). */
export type CountryEntry = {
  key: string;
  value?: string[];
  listing?: string[];
  box?: string[];
  [other: string]: unknown;
};

/** A subcategory as the categories store holds it. */
export type SubcategoryRecord = {
  _id: string;
  name: string;
  slug: string;
  properties?: CountryEntry[];
  hostsProducts?: boolean;
};

/** A category row of the screen's list. */
export type CategoryOption = {
  value: { _id: string; name: string; slug: string; subcategories?: SubcategoryRecord[] };
  label: string;
};

export type CategoryFormState = {
  activeEntity: { type: "category" | "subcategory" | null; id: string | null };
  /** which edit panel is open; one field, so two panels can never be open at once */
  editor: "none" | "category" | "subcategory";
  pendingChangeReady: boolean;
  /** field keys the editor unlocked against a pending change (an array: Immer drafts no Set without enableMapSet) */
  unlockedFields: string[];

  idCategory: string;
  category: { name: string; slug: string };
  categoryValidation: { name: boolean; slug: boolean };

  idSubcategory: string;
  subcategory: { name: string; slug: string; properties: CountryEntry[]; hostsProducts: boolean };
  subcategoryValidation: { name: boolean; slug: boolean; parentcategorie: boolean | null };
  labelsub: string;
  labelslugsub: string;
  /** the property ids enabled for the current site's country */
  subProperties: string[];
  /**
   * SCRUM-1778 (option B): the listing / detail-box choice is per subcategory per
   * country — parallel id arrays on the country's entry. `undefined` = the entry
   * carries no such key, which the renderer reads as "every enabled property is shown".
   */
  subListing: string[] | undefined;
  subBox: string[] | undefined;
};

const initialCategoryFormState: CategoryFormState = {
  activeEntity: { type: null, id: null },
  editor: "none",
  pendingChangeReady: true,
  unlockedFields: [],

  idCategory: "",
  category: { name: "", slug: "" },
  categoryValidation: { name: true, slug: true },

  idSubcategory: "",
  subcategory: { name: "", slug: "", properties: [], hostsProducts: false },
  subcategoryValidation: { name: true, slug: true, parentcategorie: null },
  labelsub: "",
  labelslugsub: "",
  subProperties: [],
  subListing: undefined,
  subBox: undefined,
};

// SCRUM-1778: an absent (or empty) flag array means "every enabled property is
// shown", so the first toggle materialises it from the enabled set before adding
// or removing the id. The listing renders in listing[] order (SCRUM-1779), so a
// re-ticked id goes back to its place in the enabled order, not to the end, and
// the stored order of the others is kept as it is.
const toggleFlag = (
  current: string[] | undefined,
  enabled: string[],
  id: string,
  checked: boolean
): string[] => {
  const base = current && current.length > 0 ? current : enabled;
  if (!checked) return base.filter((x) => x !== id);
  if (base.includes(id)) return base;
  const rank = enabled.indexOf(id);
  const at = rank < 0 ? -1 : base.findIndex((x) => enabled.indexOf(x) > rank);
  return at < 0 ? [...base, id] : [...base.slice(0, at), id, ...base.slice(at)];
};

export const categoryForm = createSlice({
  name: "categoryForm",
  initialState: initialCategoryFormState,
  reducers: {
    categoryOpened(state, action: PayloadAction<CategoryOption["value"]>) {
      const category = action.payload;
      state.idCategory = category._id;
      state.category = { name: category.name, slug: category.slug };
      state.editor = "category";
      state.pendingChangeReady = false;
      state.unlockedFields = [];
      state.activeEntity = { type: "category", id: category._id };
    },

    subcategoryOpened(
      state,
      action: PayloadAction<{
        subcategory: SubcategoryRecord;
        countryCode: string | undefined;
        label: string;
        labelSlug: string;
      }>
    ) {
      const { subcategory: ch, countryCode, label, labelSlug } = action.payload;
      const entry = ch.properties?.filter((p) => p.key === countryCode)[0];
      state.subcategory = {
        name: ch.name,
        slug: ch.slug,
        properties: ch.properties || [],
        // SCRUM-1668: seeded from the record on every open, like the label —
        // a production document without the key reads as not-a-trunk.
        hostsProducts: ch.hostsProducts === true,
      };
      state.idSubcategory = ch._id;
      state.subProperties = entry?.value || [];
      state.subListing = Array.isArray(entry?.listing) ? entry.listing : undefined;
      state.subBox = Array.isArray(entry?.box) ? entry.box : undefined;
      // SCRUM-1412 / SCRUM-1851: the label fields are seeded for THIS site on
      // every open (the caller passes "" when the site has no overlay row), so
      // the previous subcategory's typed label is never written onto this one.
      state.labelsub = label;
      state.labelslugsub = labelSlug;
      state.editor = "subcategory";
      state.pendingChangeReady = false;
      state.unlockedFields = [];
      state.activeEntity = { type: "subcategory", id: ch._id };
    },

    pendingChangeLoaded(state) {
      state.pendingChangeReady = true;
    },

    fieldUnlocked(state, action: PayloadAction<string>) {
      if (!state.unlockedFields.includes(action.payload)) state.unlockedFields.push(action.payload);
    },

    categoryFieldChanged(state, action: PayloadAction<{ name: string; value: string }>) {
      const { name, value } = action.payload;
      if (name === "name") {
        state.category.name = value;
        state.category.slug = StandardizationString(value);
      } else if (name === "slug") {
        state.category.slug = value;
      }
      state.categoryValidation = { ...state.categoryValidation, [name]: true };
    },

    subcategoryFieldChanged(
      state,
      action: PayloadAction<{ name: string; value: string; checked: boolean }>
    ) {
      const { name, value, checked } = action.payload;
      if (name === "namesub") {
        state.subcategory.name = value;
        state.subcategory.slug = StandardizationString(value);
        state.subcategoryValidation.name = true;
      } else if (name === "slugsub") {
        state.subcategory.slug = value;
      } else if (name === "labelsub") {
        state.labelsub = value;
      } else if (name === "labelslugsub") {
        state.labelslugsub = value;
      } else if (name === "hostsproductssub") {
        state.subcategory.hostsProducts = checked;
      }
    },

    propertyAdded(state, action: PayloadAction<string>) {
      // the Set also drops a duplicate the stored entry may already carry
      state.subProperties = [...new Set([...state.subProperties, action.payload])];
    },

    // Pinned as observed (updateSubcategory.payloadPin a): removing a property
    // leaves its id in listing[] / box[].
    propertyRemoved(state, action: PayloadAction<string>) {
      state.subProperties = state.subProperties.filter((id) => id !== action.payload);
    },

    // SCRUM-1778: the two visibility toggles edit local form state only; the
    // Update button saves them with the rest of the entry. They used to dispatch
    // updateProperty on click, which rewrote the SHARED Property — one value per
    // site for every subcategory, plus every other field of it.
    listingToggled(state, action: PayloadAction<{ key: string; checked: boolean }>) {
      const { key, checked } = action.payload;
      state.subListing = toggleFlag(state.subListing, state.subProperties, key, checked);
    },

    boxToggled(state, action: PayloadAction<{ key: string; checked: boolean }>) {
      const { key, checked } = action.payload;
      state.subBox = toggleFlag(state.subBox, state.subProperties, key, checked);
    },

    serverErrorsReceived(state, action: PayloadAction<Array<{ param: string; value: unknown }>>) {
      state.categoryValidation = markInvalidFields(state.categoryValidation, action.payload);
      state.subcategoryValidation = markInvalidFields(state.subcategoryValidation, action.payload);
    },

    deleteConfirmed(state) {
      state.editor = "none";
    },

    closed(state) {
      state.editor = "none";
      state.subProperties = [];
      state.subListing = undefined;
      state.subBox = undefined;
      state.pendingChangeReady = true;
      state.activeEntity = { type: null, id: null };
    },
  },
});
